import { Button, Modal, notification, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import api from "../../api";
import { AutocategorizationRuleFragment } from "../../api/graphql/fragments/autocategorizationRule.generated";
import { useCreateRecurringPaymentMutation } from "../../api/graphql/mutations/recurringPayment";
import {
  CreateAutocategorizationRuleInput,
  RuleConditionNumberOp,
  RuleConditionStringOp,
} from "../../api/graphql/schema.generated";
import { ITransaction } from "../../types";
import {
  allCategoriesRaw,
  categoryCodeCodec,
  formatAmountInCents,
  getCategoryName,
  showGraphQlErrorNotification,
  vatCategories,
  vatCategoryCodeCodec,
} from "../../utils";
import RuleForm from "../pages/RulesView/RuleForm";

export type RecurringRuleTransaction = Pick<
  ITransaction,
  | "id"
  | "name"
  | "iban"
  // Verwendungszweck. Shown in the summary only; it is deliberately not
  // pre-filled as a rule condition (SMT-1236).
  | "description"
  | "amount"
  | "categoryCode"
  | "vatCategoryCode"
  // Customer email — resolved to the account id so the rule keeps its
  // per-customer scope (conditions.accountIds).
  | "email"
>;

// id shared between the RuleForm's <form> and the modal footer's submit button
// so the footer's Create button drives the form (antd form association).
const RECURRING_RULE_FORM_ID = "recurringRuleForm";

// Category label without the leading code number, for the pre-filled rule name
// (e.g. "2292 Fachliteratur" -> "Fachliteratur").
const categoryLabelForName = (categoryCode?: string | null): string => {
  if (!categoryCode) {
    return "";
  }
  const label = getCategoryName(categoryCode, allCategoriesRaw);
  if (!label || label === "INVALID CATEGORY CODE") {
    return "";
  }
  return label.replace(/^\d+\s*/, "");
};

// VAT category label (e.g. "11 Abziehbare VSt 19 %") for the summary panel.
// transaction.vatCategoryCode is the human code, so encode it to the enum the
// vatCategories list is keyed by.
const vatCategoryLabel = (vatCategoryCode?: string | null): string => {
  if (!vatCategoryCode) {
    return "";
  }
  const vatEnum = vatCategoryCodeCodec.encode(vatCategoryCode);
  if (!vatEnum) {
    return "";
  }
  const label = getCategoryName(vatEnum, vatCategories as string[][]);
  return !label || label === "INVALID CATEGORY CODE" ? "" : label;
};

/**
 * Build the pre-filled rule form draft from a transaction. Mirrors the
 * conditions the backend's createRecurringPayment derives (payee name / IBAN and
 * the amount direction), plus the transaction's category so the operator sees a
 * meaningful starting point. Unclear fields are left empty rather than guessed.
 *
 * categoryCode / vatCategoryCode are ENCODED here because RuleForm decodes
 * rule.categoryCode back to the human code the form works with.
 *
 * accountId is the customer's kontist account id: it pre-fills
 * conditions.accountIds so the rule stays scoped to that customer (matching the
 * scope createRecurringPayment sets), instead of becoming a global rule when the
 * form values overwrite the conditions on update.
 */
export const buildRecurringRuleDraft = (
  transaction: RecurringRuleTransaction,
  accountId: number | null
): AutocategorizationRuleFragment => ({
  __typename: "AutocategorizationRule",
  id: 0,
  // Pre-fill following the naming convention [Scope] / [Company] / [Buchung],
  // defaulting Scope to "C"; the operator adjusts before creating.
  description: `C / ${transaction.name ?? ""} / ${categoryLabelForName(
    transaction.categoryCode
  )}`,
  enabled: true,
  priority: 3,
  categoryCode:
    categoryCodeCodec.encode(transaction.categoryCode ?? undefined) ?? null,
  vatCategoryCode:
    vatCategoryCodeCodec.encode(transaction.vatCategoryCode ?? undefined) ??
    null,
  verify: true,
  accuracy: null,
  appliedTimesCount: null,
  isSplit: false,
  splitLines: null,
  conditions: {
    __typename: "AutocategorizationRuleConditions",
    accountIds: accountId ? [accountId] : null,
    hasEmployees: null,
    hasInvoices: null,
    hasVatNumber: null,
    ibans: transaction.iban ? [transaction.iban] : null,
    vatPaymentFrequencies: null,
    amount: {
      __typename: "RuleConditionNumber",
      op:
        transaction.amount < 0
          ? RuleConditionNumberOp.LT
          : RuleConditionNumberOp.GT,
      value: 0,
    },
    description: null,
    name: transaction.name
      ? {
          __typename: "RuleConditionString",
          op: RuleConditionStringOp.EQ,
          value: transaction.name,
        }
      : null,
  },
});

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>{label}</div>
    <div style={{ wordBreak: "break-word" }}>{value}</div>
  </div>
);

/**
 * Read-only summary of the transaction the rule is being created from. Rendered
 * in the RuleForm's left column (via children) to give the operator context and
 * to fill the space the create flow would otherwise leave empty.
 */
export const SourceTransactionSummary = ({
  transaction,
  t,
}: {
  transaction: RecurringRuleTransaction;
  t: TFunction;
}) => (
  <div
    style={{
      marginTop: 8,
      padding: 16,
      background: "#fafafa",
      border: "1px solid #f0f0f0",
      borderRadius: 4,
    }}
  >
    <div style={{ fontWeight: 600, marginBottom: 16 }}>
      {t("recurringModal.source.title")}
    </div>
    <SummaryRow
      label={t("recurringModal.source.paidToFrom")}
      value={transaction.name || "—"}
    />
    <SummaryRow
      label={t("recurringModal.source.amount")}
      value={formatAmountInCents(transaction.amount, true)}
    />
    <SummaryRow
      label={t("recurringModal.source.iban")}
      value={transaction.iban || "—"}
    />
    <SummaryRow
      label={t("recurringModal.source.description")}
      value={transaction.description || "—"}
    />
    <SummaryRow
      label={t("recurringModal.source.category")}
      value={
        transaction.categoryCode
          ? getCategoryName(transaction.categoryCode, allCategoriesRaw)
          : "—"
      }
    />
    <SummaryRow
      label={t("recurringModal.source.vatCategory")}
      value={vatCategoryLabel(transaction.vatCategoryCode) || "—"}
    />
    <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>
      {t("recurringModal.source.hint")}
    </div>
    <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)", marginTop: 8 }}>
      {t("recurringModal.source.descriptionHint")}
    </div>
  </div>
);

export interface RecurringRuleModalProps {
  transaction: RecurringRuleTransaction;
  /** Called with the created rule id once the rule has been created + saved. */
  onCreated: (ruleId: number) => void;
  /** Called when the operator cancels the modal. Nothing is written. */
  onClose: () => void;
}

/**
 * Confirm-before-create modal for recurring-payment rules. Reuses the rules-view
 * add-rule form, pre-filled from the transaction. On Create it sends one
 * createRecurringPayment request carrying the operator's edited name/conditions;
 * the backend applies them before the booking job starts and removes the rule
 * again if they are invalid, so a failed Create leaves nothing behind. Cancel
 * writes nothing and just closes the modal.
 */
const RecurringRuleModal = ({
  transaction,
  onCreated,
  onClose,
}: RecurringRuleModalProps) => {
  const { t } = useTranslation("Rules");
  // Resolve the customer's account id from the transaction email so the rule
  // keeps its per-customer scope. `undefined` = still resolving; `null` = could
  // NOT be resolved (missing email / lookup failure) — in that case we must NOT
  // create the rule, because an unscoped rule would match other customers
  // (the AC3 regression). A number means the scope is known.
  const [accountId, setAccountId] = useState<number | null | undefined>(
    undefined
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!transaction.email) {
          throw new Error("Transaction has no customer email");
        }
        const user = await api.kontax.getUserByEmail(transaction.email);
        if (!cancelled) {
          setAccountId(
            typeof user?.accountId === "number" ? user.accountId : null
          );
        }
      } catch {
        if (!cancelled) {
          setAccountId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transaction.email]);

  const resolvingAccount = accountId === undefined;
  const accountLookupFailed = accountId === null;
  const accountReady = typeof accountId === "number";

  const draft = useMemo(
    () =>
      typeof accountId === "number"
        ? buildRecurringRuleDraft(transaction, accountId)
        : null,
    [transaction, accountId]
  );
  const [createRecurringPayment] = useCreateRecurringPaymentMutation();
  const [submitting, setSubmitting] = useState(false);

  const handleCancel = () => {
    if (submitting) {
      return;
    }
    onClose();
  };

  const handleSubmit = async (input: CreateAutocategorizationRuleInput) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const created = await createRecurringPayment({
        variables: { transactionId: transaction.id, input },
      });
      const ruleId = created.data?.createRecurringPayment?.id ?? null;
      if (!ruleId) {
        throw new Error("Recurring payment rule was not created");
      }
      notification.success({ message: t("recurringModal.created") });
      onCreated(ruleId);
    } catch (err) {
      showGraphQlErrorNotification(t("recurringModal.createError"), err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      data-test="recurringRuleModal"
      open
      centered
      destroyOnClose
      width={1320}
      style={{ maxWidth: "96vw" }}
      bodyStyle={{ maxHeight: "78vh", overflowY: "auto", paddingTop: 12 }}
      maskClosable={!submitting}
      onCancel={submitting ? undefined : handleCancel}
      title={
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {t("recurringModal.title")}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: "rgba(0, 0, 0, 0.45)",
              marginTop: 2,
            }}
          >
            {t("recurringModal.subtitle")}
          </div>
        </div>
      }
      footer={[
        <Button
          key="cancel"
          data-test="recurringRuleCancel"
          onClick={handleCancel}
          disabled={submitting}
        >
          {t("recurringModal.cancel")}
        </Button>,
        <Button
          key="create"
          data-test="recurringRuleCreate"
          form={RECURRING_RULE_FORM_ID}
          htmlType="submit"
          type="primary"
          loading={submitting}
          disabled={!accountReady}
        >
          {t("recurringModal.create")}
        </Button>,
      ]}
    >
      {resolvingAccount && (
        <div style={{ padding: 48, textAlign: "center" }}>
          <Spin />
        </div>
      )}
      {accountLookupFailed && (
        <div
          data-test="recurringRuleAccountError"
          style={{ padding: 32, textAlign: "center" }}
        >
          <p style={{ fontWeight: 600 }}>
            {t("recurringModal.accountError.title")}
          </p>
          <p style={{ color: "rgba(0, 0, 0, 0.45)" }}>
            {t("recurringModal.accountError.body")}
          </p>
        </div>
      )}
      {draft && (
        <RuleForm
          rule={draft}
          loading={submitting}
          onSubmit={handleSubmit}
          hideDefaultActions
          formId={RECURRING_RULE_FORM_ID}
          leftCol={{ span: 11, offset: 0 }}
          rightCol={{ span: 11, offset: 2 }}
        >
          <SourceTransactionSummary transaction={transaction} t={t} />
        </RuleForm>
      )}
    </Modal>
  );
};

export default RecurringRuleModal;
